/**
 * projectService.js
 *
 * Thin interface for project CRUD backed by the API.
 * Keeps projectManager cache in sync so existing UI can still use
 * synchronous reads where needed.
 */

import projectManager from '../models/projects';

/**
 * Returns all projects after syncing with backend API.
 *
 * @returns {Promise<Array<{id: number, name: string, status: string, ...}>>}
 */
export async function getProjects() {
  const projects = await projectManager.refreshProjects();
  return Array.isArray(projects) ? projects : [];
}

/**
 * Returns cached projects without forcing an API request.
 *
 * @returns {Array<{id: number, name: string, status: string, ...}>}
 */
export function getCachedProjects() {
  return projectManager.getAllProjects() || [];
}

/**
 * Looks up a single project by its numeric id.
 *
 * @param {number|null} id
 * @returns {{id: number, name: string, status: string, ...}|null}
 */
export function getProjectById(id) {
  if (id == null) return null;
  return getCachedProjects().find(p => p.id === id) || null;
}

/**
 * Creates a new project via API and updates local project cache.
 *
 * @param {string} name
 * @returns {Promise<{id: number, name: string, status: string, ...}>}
 */
export async function createProject(name) {
  return projectManager.createProject({
    name: name.trim(),
    client: '',
    client_id: null,       // FK → contacts (optional)
    team: [],
    color: '#D7C8F6',
    deadline: '',
    status: 'active',
    description: '',
    budget: 0,
    notes: '',
    start_date: null,
    end_date: null,
    created_at: new Date().toISOString(),
  });
}

// ─── Expense data model (Block 2 prep) ───────────────────────────────────────
//
// Expenses UI is not yet built. The schema below reserves project_id as a
// nullable FK so the data layer is ready when Block 2 is implemented.
//
// Expenses will be stored under localStorage key "expenses".
//
// Expected shape:
//   {
//     id:          number   (Date.now()),
//     description: string,
//     amount:      number   (GBP),
//     date:        string   (ISO date),
//     category:    string,
//     contact_id:  number | null,   // FK → contacts
//     project_id:  number | null,   // FK → projects  ← reserved here
//     receipt_url: string | null,   // future: file upload
//     notes:       string,
//     created_at:  string,          // ISO datetime
//   }

export const EXPENSE_DEFAULTS = {
  id: null,
  description: '',
  amount: 0,
  date: '',
  category: '',
  contact_id: null,
  project_id: null,   // nullable FK → projects
  receipt_url: null,
  notes: '',
  created_at: null,
};
